import { useState } from 'react';
import { Button } from '@klaro7/ui';

import { PhoneVerification } from './PhoneVerification';
import { useCancelPendingPhone } from '../hooks/usePhoneVerification';

interface Props {
  phone?: string;
  phoneVerified?: boolean;
  pendingPhone?: string;
  /** Called after any change (verify / cancel) so the caller can refresh the user. */
  onChanged: () => void;
}

/**
 * Phone number + verification management for the profile pages.
 * - Not verified yet → verify the current number (onboarding).
 * - Verified → show the active number and let the user request a NEW one. The
 *   active number stays in use until the new one is verified (pending state).
 */
export function PhoneChangeCard({ phone, phoneVerified, pendingPhone, onChanged }: Props) {
  const [changing, setChanging] = useState(false);
  const cancelMut = useCancelPendingPhone();

  const handleCancel = async () => {
    await cancelMut.mutateAsync();
    onChanged();
  };

  // First-time verification (or never verified): verify the current number.
  if (!phoneVerified) {
    return (
      <div className="space-y-2">
        <label className="mb-1.5 block text-sm font-medium text-neutral-700">Telefono</label>
        <PhoneVerification initialPhone={phone ?? ''} onVerified={onChanged} />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div>
        <label className="mb-1.5 block text-sm font-medium text-neutral-700">Telefono</label>
        <div className="flex items-center gap-2">
          <span className="font-medium text-neutral-900">{phone}</span>
          <span className="text-sm font-medium text-green-600">verificato ✓</span>
        </div>
      </div>

      {pendingPhone ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="text-sm font-medium text-amber-800">
            Nuovo numero <span className="font-semibold">{pendingPhone}</span> in attesa di verifica
          </p>
          <p className="mt-0.5 text-xs text-amber-700">
            Il numero attuale resta attivo finché non verifichi il nuovo.
          </p>
          <div className="mt-3">
            <PhoneVerification initialPhone={pendingPhone} autoStart onVerified={onChanged} />
          </div>
          <Button variant="outline" size="sm" className="mt-2" onClick={handleCancel} loading={cancelMut.isPending}>
            Annulla cambio numero
          </Button>
        </div>
      ) : changing ? (
        <div className="rounded-lg border border-neutral-200 p-3">
          <p className="mb-2 text-sm text-neutral-600">Inserisci il nuovo numero da verificare.</p>
          <PhoneVerification onVerified={() => { setChanging(false); onChanged(); }} />
          <Button variant="ghost" size="sm" className="mt-2" onClick={() => setChanging(false)}>
            Chiudi
          </Button>
        </div>
      ) : (
        <Button variant="outline" size="sm" onClick={() => setChanging(true)}>
          Cambia numero
        </Button>
      )}
    </div>
  );
}
